import { Button, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';
import {
  disconnectErp,
  IntacctEntity,
  listIntacctEntities,
  selectIntacctEntity,
} from 'app/services/erp-connection.service';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import IntacctEntityPickerModal from './IntacctEntityPickerModal';

interface IntacctEntityPickerReturnFlowProps {
  /** The paidol the returning OAuth connection belongs to. */
  paidolId: string | undefined;
}

/**
 * In-flight de-dup for the entity listing. The onboarding boot unmounts/remounts
 * this component several times in quick succession (PurchaseCardsWrapper's
 * loading overlay toggles its children while data loads); every mount detects
 * the param and would otherwise fire its own live Sage entity-list call. Sharing
 * the in-flight promise per connection collapses that burst into a single
 * network call — every instance (including the one that ends up stable) awaits
 * the same promise. The entry is dropped once the call settles (success or
 * failure), so it only de-dups concurrent calls and never serves a stale list.
 */
const entityListInFlight = new Map<string, Promise<IntacctEntity[]>>();

function listEntitiesOnce(paidolId: string, connectionId: string): Promise<IntacctEntity[]> {
  const key = `${paidolId}:${connectionId}`;
  let promise = entityListInFlight.get(key);
  if (!promise) {
    promise = listIntacctEntities(paidolId, connectionId).finally(() => {
      entityListInFlight.delete(key);
    });
    entityListInFlight.set(key, promise);
  }
  return promise;
}

/**
 * Self-contained handler for the post-OAuth "choose an entity" step (SEE-1062).
 *
 * When a top-level Sage user finishes OAuth, the callback redirects back with
 * `?intacct_auth=needs_entity_selection&connection_id=...`. This component
 * detects that, lists the company's entities, and either auto-resolves (0/1
 * entity) or shows the picker (>1). It owns the whole flow — listing, the modal,
 * confirm/cancel/refresh — so it can be dropped into BOTH the Settings
 * connectivity page and the onboarding stepper without duplicating logic.
 *
 * Cancelling has no usable "connected" state (the sync refuses a PENDING
 * paidol), so it disconnects this paidol via erpDisconnect (which only unlinks
 * this paidol and tears the shared connection down when it was the last user).
 */
function IntacctEntityPickerReturnFlow({ paidolId }: IntacctEntityPickerReturnFlowProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const handledRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [entities, setEntities] = useState<IntacctEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pendingConnectionId, setPendingConnectionId] = useState<string | null>(null);

  // Persist the chosen entity (or activate company-wide — via topLevel for an
  // explicit multi-entity all-entities choice (SEE-1261), or an omitted entityId
  // for a single-entity company) and reload into the now-ACTIVE connected state.
  const finalize = useCallback(
    async (connectionId: string, entityId?: string, topLevel?: boolean) => {
      if (!paidolId) return;
      setSubmitting(true);
      try {
        await selectIntacctEntity(paidolId, connectionId, entityId, topLevel);
        enqueueSnackbar(t('erpConnection.intacctConnected'), { variant: 'success' });
        // Reload to a param-free URL so the picker doesn't re-trigger off the
        // still-present intacct_auth param after the connection is resolved.
        window.location.replace(window.location.pathname);
      } catch (error) {
        setSubmitting(false);
        enqueueSnackbar(
          `${t('erpConnection.intacctConnectionFailed')}: ${
            error instanceof Error ? error.message : 'unknown'
          }`,
          { variant: 'error' }
        );
      }
    },
    [paidolId, enqueueSnackbar, t]
  );

  const intacctAuthResult = searchParams.get('intacct_auth');
  useEffect(() => {
    if (handledRef.current) return;
    if (intacctAuthResult !== 'needs_entity_selection') return;

    // The OAuth callback returns via a full-page redirect, so the app reboots
    // and `selectedPaidol` (→ paidolId) is repopulated from the store
    // asynchronously — it is undefined on the first render(s). Wait for it
    // before consuming the one-shot (handledRef); otherwise a paidol-less mount
    // would mark this handled and never list once paidolId hydrates. The effect
    // re-runs when paidolId arrives.
    if (!paidolId) return;

    handledRef.current = true;
    const connectionId = searchParams.get('connection_id');

    // NOTE: we deliberately do NOT strip the query params here. During the
    // onboarding boot, the parent subtree (PurchaseCardsWrapper's loading
    // overlay) unmounts/remounts this component several times. If we stripped
    // the param on the first (throwaway) mount, every later instance would see
    // a bare URL and stay inert, and the in-flight listing would resolve into a
    // dead instance. Leaving the param lets whichever instance ends up stable
    // re-read it and show the modal. The param is cleared by the reload in
    // finalize()/handleCancelConfirm() once the user actually resolves it.
    if (!connectionId) {
      enqueueSnackbar(t('erpConnection.intacctConnectionFailed'), { variant: 'error' });
      return;
    }

    setPendingConnectionId(connectionId);
    // Open immediately with a loading state — listing entities is a live Sage
    // round-trip, so showing the modal-with-spinner avoids a blank page after
    // the redirect. 0/1-entity cases auto-resolve below.
    setLoading(true);
    setOpen(true);
    (async () => {
      try {
        const list = await listEntitiesOnce(paidolId, connectionId);
        if (list.length > 1) {
          setEntities(list);
          setLoading(false);
        } else if (list.length === 1) {
          await finalize(connectionId, list[0].id);
        } else {
          // Single-entity company — nothing to scope; activate company-wide.
          await finalize(connectionId);
        }
      } catch (error) {
        setOpen(false);
        setLoading(false);
        enqueueSnackbar(
          `${t('erpConnection.intacctConnectionFailed')}: ${
            error instanceof Error ? error.message : 'unknown'
          }`,
          { variant: 'error' }
        );
      }
    })();
  }, [intacctAuthResult, searchParams, enqueueSnackbar, t, paidolId, finalize]);

  const handleConfirm = (entityId: string | null, topLevel: boolean) => {
    if (pendingConnectionId) finalize(pendingConnectionId, entityId ?? undefined, topLevel);
  };

  const handleRefresh = useCallback(async () => {
    if (!pendingConnectionId || !paidolId) return;
    setRefreshing(true);
    try {
      setEntities(await listIntacctEntities(paidolId, pendingConnectionId));
    } catch (error) {
      enqueueSnackbar(
        `${t('erpConnection.intacctConnectionFailed')}: ${
          error instanceof Error ? error.message : 'unknown'
        }`,
        { variant: 'error' }
      );
    } finally {
      setRefreshing(false);
    }
  }, [pendingConnectionId, paidolId, enqueueSnackbar, t]);

  const handleCancelConfirm = useCallback(async () => {
    if (!paidolId || !pendingConnectionId) return;
    setCancelling(true);
    try {
      await disconnectErp(paidolId, pendingConnectionId);
      enqueueSnackbar(t('erpConnection.disconnected'), { variant: 'success' });
      // Reload to a param-free URL (see finalize()).
      window.location.replace(window.location.pathname);
    } catch (error) {
      setCancelling(false);
      setCancelConfirmOpen(false);
      enqueueSnackbar(t('erpConnection.disconnectFailed'), { variant: 'error' });
    }
  }, [paidolId, pendingConnectionId, enqueueSnackbar, t]);

  return (
    <>
      <IntacctEntityPickerModal
        open={open}
        entities={entities}
        submitting={submitting}
        loading={loading}
        refreshing={refreshing}
        onConfirm={handleConfirm}
        onRefresh={handleRefresh}
        onCancel={() => setCancelConfirmOpen(true)}
        cancelling={cancelling}
      />

      {/* Confirm before cancelling — cancelling disconnects this paidol (no
          usable connection without an entity). */}
      <ResponsiveDialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)}>
        <DialogTitle>{t('erpConnection.cancelEntityTitle', 'Cancel connection?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'erpConnection.cancelEntityConfirm',
              "Without selecting an entity, Sage Intacct won't be connected. Cancelling will disconnect it — you can reconnect anytime."
            )}
          </DialogContentText>
        </DialogContent>
        <ResponsiveDialogActions>
          <Button disabled={cancelling} onClick={() => setCancelConfirmOpen(false)}>
            {t('erpConnection.keepSelecting', 'Keep selecting')}
          </Button>
          <Button color="error" variant="contained" disabled={cancelling} onClick={handleCancelConfirm}>
            {cancelling ? t('loading') : t('erpConnection.cancelAndDisconnect', 'Cancel & disconnect')}
          </Button>
        </ResponsiveDialogActions>
      </ResponsiveDialog>
    </>
  );
}

export default IntacctEntityPickerReturnFlow;
